import { useState } from 'react';
import { MonthlyFmtTaskEditor } from '@/components/MonthlyFmtTaskEditor';
import { MonthlyRunDialog } from '@/components/MonthlyRunDialog';
import { FmtPartsCells } from '@/components/FmtPartsCells';
import {
  buildMonthlyTasks,
  hasMonthlyTasks,
  resolveDueDate,
  withoutExisting,
  workMonthOf,
} from '@/fmt/generate-tasks';
import { useMonthlyFmtItems } from '@/api/use-monthly-fmt';
import { MONTHLY_FMT, type FmtVariant } from '@/fmt/fmt-variant';
import type { MonthlyFmtTask } from '@cortex/ado-task-fmt';
import { closingMonth, formatYearMonth } from '@/tasks/calendar-range';
import type { Task } from '@/tasks/task';

/** 「すべて」を表す値。事業名・担当者名と衝突しないよう空文字を使う */
/** @graph-connects none */
export const ALL = '';

/**
 * 大分類ごとにまとめたタスク。
 */
export interface CategoryGroup {
  /** 大分類（`00.前準備` など） */
  category: string;
  /** その大分類のタスク */
  tasks: MonthlyFmtTask[];
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 大分類の中の並びを、予定の日付が早い順にする。同じ日のものは内容の頭文字順
 * @graph-connects resolveDueDate [calls] 予定の書き方を実際の日付に直す
 */
export function sortByDueDate(
  tasks: readonly MonthlyFmtTask[],
  workMonth: string,
): MonthlyFmtTask[] {
  // 「04営業日」と「毎月10日」は書き方が違うだけで、どちらもその月の 1 日を指す。
  // 実際の日付に直してから比べないと、文字列の並びでは前後が入れ替わる
  return [...tasks].sort((a, b) => {
    const dayA = resolveDueDate(a.dueRule, workMonth);
    const dayB = resolveDueDate(b.dueRule, workMonth);
    return dayA === dayB ? a.title.localeCompare(b.title, 'ja') : dayA.localeCompare(dayB);
  });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクを大分類ごとにまとめ、大分類の番号順に並べる。中は予定の日付順
 * @graph-connects sortByDueDate [calls] 大分類の中を予定の日付順に並べる
 */
export function groupByCategory(
  tasks: readonly MonthlyFmtTask[],
  workMonth: string,
): CategoryGroup[] {
  const byCategory = new Map<string, MonthlyFmtTask[]>();
  for (const task of tasks) {
    const same = byCategory.get(task.majorCategory);
    if (same === undefined) {
      byCategory.set(task.majorCategory, [task]);
    } else {
      same.push(task);
    }
  }
  // 大分類は `00.` `01.` … `99.` と 2 桁で始まるので、文字列の並びがそのまま番号順になる
  return [...byCategory.entries()]
    .map(([category, grouped]) => ({ category, tasks: sortByDueDate(grouped, workMonth) }))
    .sort((a, b) => a.category.localeCompare(b.category, 'ja'));
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 値の種類を重複なく名前順で返す。絞り込みと入力候補に使う
 * @graph-connects none
 */
export function listValues(
  tasks: readonly MonthlyFmtTask[],
  pick: (task: MonthlyFmtTask) => string,
): string[] {
  return [...new Set(tasks.map(pick))]
    .filter((value) => value !== '')
    .sort((a, b) => a.localeCompare(b, 'ja'));
}

/**
 * 大分類の工程ごとの色。
 *
 * 大分類は 24 区分あり、全部に色を振ると意味を失う。番号の桁で「準備・入出金 → 計上 →
 * 確認 → 締め・共有」という月次の流れに畳んで 4 色にする。色は既存の種別色を流用する。
 * @graph-connects none
 */
const PHASE_COLOR: readonly { upTo: number; color: string; background: string }[] = [
  { upTo: 9, color: 'var(--color-doing-border)', background: 'var(--color-doing-bg)' },
  { upTo: 39, color: 'var(--color-kind-monthly)', background: 'var(--color-kind-monthly-bg)' },
  { upTo: 49, color: 'var(--color-kind-meeting)', background: 'var(--color-kind-meeting-bg)' },
  { upTo: 99, color: 'var(--color-kind-closing)', background: 'var(--color-kind-closing-bg)' },
];

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 大分類の色を、先頭の番号から引く。番号が読めないものはニュートラルに倒す
 * @graph-connects none
 */
export function phaseColor(category: string): { color: string; background: string } {
  const number = Number(/^(\d{1,2})\./.exec(category)?.[1] ?? NaN);
  if (Number.isNaN(number)) {
    return { color: 'var(--color-border-subtle)', background: 'var(--color-bg-base)' };
  }
  const phase = PHASE_COLOR.find((entry) => number <= entry.upTo);
  return phase ?? { color: 'var(--color-border-subtle)', background: 'var(--color-bg-base)' };
}

/** 対象月に選べる年。いまの期の前後を出しておけば足りる */
/** @graph-connects none */
const YEARS = ['2025', '2026', '2027', '2028'];

/** 1〜12 月 */
/** @graph-connects none */
const MONTHS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'));

/** @graph-connects none */
const SELECT_CLASS =
  'rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2.5 py-1.5 text-sm text-[var(--color-text-secondary)]';

/** 編集の状態。`null` は新規追加、`undefined`（state 側）は閉じている */
type EditTarget = MonthlyFmtTask | null;

/**
 * MonthlyAccounting の props。
 */
export interface MonthlyAccountingProps {
  /** すでにあるタスク。同じ月を二重に作らない判定に使う */
  existingTasks: readonly Task[];
  /** タスクを作成するときに呼ばれる。保存が終わるまで待てるよう、書けたかどうかを返す */
  onCreateTasks: (created: readonly Omit<Task, 'id'>[]) => Promise<boolean>;
  /** 作成対象の初期表示月（`YYYY-MM`）。既定はいま締めている月（前月） */
  initialMonth?: string;
  /** どの FMT を扱うか。既定は月次経理。決算タスクFMT も同じ画面で扱う */
  variant?: FmtVariant;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business FMT（月次経理・決算タスクFMT）の一覧。項目の追加・編集・削除ができ、対象月を選んでタスク管理へタスクを起こす
 * @graph-connects groupByCategory [calls] 大分類ごとにまとめる
 * @graph-connects buildMonthlyTasks [calls] その月のタスクを作る
 * @graph-connects workMonthOf [calls] 締める月を出し、並びの基準にする
 * @graph-connects withoutExisting [calls] すでに作成済みのものを除く
 * @graph-connects MonthlyFmtTaskEditor [calls] 追加・編集のポップアップを出す
 * @graph-connects FmtPartsCells [calls] 事業・中分類を丸いプルダウンで選び直させる
 * @graph-connects closingMonth [calls] いま締めている月を既定の対象月に使う
 */
export function MonthlyAccounting({
  existingTasks,
  onCreateTasks,
  // 開いたら、いま締めている月が選ばれているようにする
  initialMonth = closingMonth(),
  variant = MONTHLY_FMT,
}: MonthlyAccountingProps) {
  const { items, loading, error, loadError, add, update, remove } = useMonthlyFmtItems(
    variant.apiPath,
  );
  const period = (target: string) => `${formatYearMonth(target)}${variant.periodSuffix}`;
  const [business, setBusiness] = useState(ALL);
  const [assignee, setAssignee] = useState(ALL);
  const [editing, setEditing] = useState<EditTarget | undefined>(undefined);
  // 新しく足すときに、はじめから入れておく値（大分類の見出しから足したとき）
  const [newDefaults, setNewDefaults] = useState<Partial<MonthlyFmtTask>>({});
  // 削除は取り返しがつかないので、押した行だけ確認に切り替える
  const [confirmingId, setConfirmingId] = useState('');
  const [creating, setCreating] = useState(false);
  const [month, setMonth] = useState(initialMonth);
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  const tasks = items.filter(
    (task) =>
      (business === ALL || task.business === business) &&
      (assignee === ALL || task.assignee === assignee),
  );
  // 並びは「締める月」の日付で決める。10 月分を締めるのは 11 月なので、
  // タスクを起こしたときに並ぶ順と、この一覧の順が揃う
  const groups = groupByCategory(tasks, workMonthOf(month));
  // その月のタスクを既に起こしていれば、もう作らせない（二重に並ぶと消す手間が大きい）
  const createdMonth = hasMonthlyTasks(existingTasks, month, variant);
  const totalHours = tasks.reduce((sum, task) => sum + (task.estimatedHours ?? 0), 0);

  const removeItem = (id: string) => {
    void remove(id);
    setConfirmingId('');
    setEditing(undefined);
  };

  const save = (saved: MonthlyFmtTask) => {
    // 失敗したときの表示は下の帯が担うので、ここでは投げっぱなしにする
    void (saved.id === '' ? add(saved) : update(saved));
    setEditing(undefined);
    setNotice('');
  };

  const create = async (dateByRule: Record<string, string>) => {
    setCreating(false);
    // 作成済みの月はここでも止める。絞り込みを変えて押し直すと、同じ月に足せてしまうため
    if (createdMonth) {
      setNotice(`${period(month)}のタスクはすでに作成済みです`);
      return;
    }
    const candidates = buildMonthlyTasks(tasks, month, dateByRule, variant);
    const created = withoutExisting(candidates, existingTasks);
    if (created.length === 0) {
      setNotice(`${period(month)}のタスクはすでに作成済みです`);
      return;
    }
    // 保存が終わってから知らせる。失敗したときは画面上部に理由が出る
    setSaving(true);
    const saved = await onCreateTasks(created);
    setSaving(false);
    if (!saved) {
      // 途中で失敗したら「作成しました」とは言わない。何件入ったかはタスク管理で見てもらう
      setNotice('');
      return;
    }
    setNotice(
      `${period(month)}のタスクを ${String(created.length)} 件作成しました（タスク管理で開けます）`,
    );
  };

  return (
    <main className="min-w-0 flex-1 overflow-y-auto px-8 py-5">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="m-0 text-xs font-bold tracking-wide text-[var(--color-text-secondary)]">
            {variant.section}
          </p>
          <h1 className="mt-0.5 mb-0 text-lg font-semibold tracking-tight text-[var(--color-text-primary)]">
            FMT
          </h1>
          <p className="mt-0.5 mb-0 text-xs text-[var(--color-text-secondary)]">
            {variant.description}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="fmt-business" className="sr-only">
            事業で絞り込む
          </label>
          <select
            id="fmt-business"
            value={business}
            onChange={(event) => {
              setBusiness(event.target.value);
            }}
            className={SELECT_CLASS}
          >
            <option value={ALL}>事業：すべて</option>
            {listValues(items, (task) => task.business).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <label htmlFor="fmt-assignee" className="sr-only">
            担当者で絞り込む
          </label>
          <select
            id="fmt-assignee"
            value={assignee}
            onChange={(event) => {
              setAssignee(event.target.value);
            }}
            className={SELECT_CLASS}
          >
            <option value={ALL}>担当：すべて</option>
            {listValues(items, (task) => task.assignee).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => {
              setNewDefaults({});
              setEditing(null);
            }}
            className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)]"
          >
            項目を追加
          </button>
        </div>
      </header>

      {/* タスクを起こす操作。対象月と、いま表示している件数が見える位置に置く */}
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-4 py-3">
        <label htmlFor="fmt-month" className="text-sm text-[var(--color-text-secondary)]">
          {variant.periodLabel}
        </label>
        {/* ブラウザ既定の月入力は「September 2026」と英語で出るため、年と月の
            プルダウンを自前で置く */}
        <select
          id="fmt-month"
          value={month.slice(0, 4)}
          onChange={(event) => {
            setMonth(`${event.target.value}-${month.slice(5, 7)}`);
            setNotice('');
          }}
          className={SELECT_CLASS}
        >
          {YEARS.map((year) => (
            <option key={year} value={year}>
              {year}年
            </option>
          ))}
        </select>
        <label htmlFor="fmt-month-number" className="sr-only">
          {variant.periodLabel}の月
        </label>
        <select
          id="fmt-month-number"
          value={month.slice(5, 7)}
          onChange={(event) => {
            setMonth(`${month.slice(0, 4)}-${event.target.value}`);
            setNotice('');
          }}
          className={SELECT_CLASS}
        >
          {MONTHS.map((value) => (
            <option key={value} value={value}>
              {Number(value)}月
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={createdMonth}
          onClick={() => {
            setNotice('');
            setCreating(true);
          }}
          className={
            createdMonth
              ? 'rounded bg-[var(--color-bg-subtle)] px-3 py-1.5 text-sm font-medium text-[var(--color-text-muted)]'
              : 'cursor-pointer rounded bg-[var(--color-accent)] px-3 py-1.5 text-sm font-medium text-[var(--color-bg-surface)]'
          }
        >
          タスクを作成する
        </button>
        <span className="text-xs text-[var(--color-text-muted)]">
          {saving
            ? '作成しています…'
            : createdMonth
              ? `${period(month)}のタスクは作成済みです（タスク管理で開けます）`
              : `押すと、締める月の営業日を確認する画面が出ます（表示中の ${String(tasks.length)} 件）`}
        </span>
        {notice === '' ? null : (
          <span className="rounded bg-[var(--color-doing-bg)] px-2.5 py-1 text-xs text-[var(--color-doing-text)]">
            {notice}
          </span>
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-4 text-sm text-[var(--color-text-muted)]">
        <span>
          <span className="tabular-nums text-[var(--color-text-primary)]">{tasks.length}</span>件
        </span>
        <span>
          想定{' '}
          <span className="tabular-nums text-[var(--color-text-primary)]">
            {totalHours.toFixed(2)}
          </span>
          時間
        </span>
      </div>

      {error === '' ? null : (
        <p className="mb-3 rounded border border-[var(--color-doing-border)] bg-[var(--color-doing-bg)] px-4 py-2 text-sm text-[var(--color-doing-text)]">
          {error}
        </p>
      )}

      {/* 読み込み中の知らせを出すのは、まだ出せる一覧が無いときだけ。
          書いたあとの読み直しでも出すと、一覧が 1 行に縮んでページが短くなり、
          ブラウザがスクロール位置を先頭へ戻してしまう */}
      {loading && items.length === 0 ? (
        <p className="py-16 text-center text-sm text-[var(--color-text-muted)]">
          読み込んでいます…
        </p>
      ) : items.length === 0 && loadError === '' ? (
        /* 読めたうえで空のとき。型は Firestore に入れてあるので、ここへ来るのは
           消しきったときだけ。読めていないときは上の帯が理由を出す */
        <div className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-6 py-8 text-center">
          <p className="m-0 text-sm text-[var(--color-text-primary)]">
            {variant.name}がまだ入っていません
          </p>
          <p className="mt-1 mb-0 text-xs leading-relaxed text-[var(--color-text-secondary)]">
            右上の「項目を追加」から作れます
          </p>
        </div>
      ) : tasks.length === 0 ? (
        <p className="py-16 text-center text-sm text-[var(--color-text-muted)]">
          条件に合うタスクはありません
        </p>
      ) : (
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)]">
              <th className="w-24 py-2 pr-3 font-medium text-[var(--color-text-muted)]">事業</th>
              <th className="w-40 py-2 pr-3 font-medium text-[var(--color-text-muted)]">中分類</th>
              <th className="py-2 pr-3 font-medium text-[var(--color-text-muted)]">内容</th>
              <th className="w-24 py-2 pr-3 font-medium text-[var(--color-text-muted)]">予定</th>
              <th className="w-16 py-2 pr-3 text-right font-medium text-[var(--color-text-muted)]">
                想定
              </th>
              <th className="w-24 py-2 pr-3 font-medium text-[var(--color-text-muted)]">担当</th>
              <th className="w-24 py-2">
                <span className="sr-only">削除</span>
              </th>
            </tr>
          </thead>
          {groups.map((group) => (
            <tbody key={group.category}>
              {/* 大分類は行をまたぐ見出しにする。列に入れると同じ値が延々と続いて読みにくい */}
              <tr className="border-b border-[var(--color-border-subtle)]">
                <th
                  colSpan={7}
                  style={{
                    backgroundColor: phaseColor(group.category).background,
                    borderLeft: `3px solid ${phaseColor(group.category).color}`,
                  }}
                  className="py-1.5 pr-2 pl-2 text-left text-sm font-bold text-[var(--color-text-primary)]"
                >
                  <span className="flex items-center gap-2">
                    <span className="min-w-0 flex-1">
                      {group.category}
                      <span className="ml-2 text-xs font-normal text-[var(--color-text-secondary)]">
                        {group.tasks.length}件
                      </span>
                    </span>
                    {/* 大分類の見出しから足せるようにする。上の「項目を追加」から足すと
                        大分類を毎回選び直すことになり、表記のゆれも生まれる */}
                    <button
                      type="button"
                      aria-label={`${group.category}に項目を追加`}
                      title={`${group.category}に項目を追加`}
                      onClick={() => {
                        setNewDefaults({ majorCategory: group.category });
                        setEditing(null);
                      }}
                      className="shrink-0 cursor-pointer rounded px-2 py-0.5 text-sm font-normal text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-surface)]"
                    >
                      ＋ 追加
                    </button>
                  </span>
                </th>
              </tr>
              {group.tasks.map((task) => (
                <tr
                  key={task.id}
                  onClick={() => {
                    setEditing(task);
                  }}
                  className="cursor-pointer border-b border-[var(--color-border-subtle)] hover:bg-[var(--color-bg-surface)]"
                >
                  {/* 事業・中分類は詳細と同じ丸いプルダウンで出し、行を開かずに選び直せる */}
                  <FmtPartsCells
                    item={task}
                    items={items}
                    onChange={(changed) => {
                      // 失敗したときの表示は下の帯が担う
                      void update(changed);
                    }}
                  />
                  <td className="py-2 pr-3 align-top text-[var(--color-text-primary)]">
                    {task.title}
                  </td>
                  <td className="py-2 pr-3 align-top whitespace-nowrap tabular-nums text-[var(--color-text-secondary)]">
                    {task.dueRule}
                  </td>
                  <td className="py-2 pr-3 align-top text-right tabular-nums text-[var(--color-text-secondary)]">
                    {task.estimatedHours === null ? '—' : task.estimatedHours.toFixed(2)}
                  </td>
                  <td className="py-2 pr-3 align-top text-[var(--color-text-secondary)]">
                    {task.assignee}
                  </td>
                  {/* 削除は行を開かずに済ませたいので右端に置く。押すとその行だけ確認に変わる */}
                  <td
                    className="py-2 text-right align-top"
                    onClick={(event) => {
                      // 行のクリック（編集を開く）と重ならないようにする
                      event.stopPropagation();
                    }}
                  >
                    {confirmingId === task.id ? (
                      <span className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            removeItem(task.id);
                          }}
                          className="rounded border border-[var(--color-border-subtle)] px-1.5 py-0.5 text-xs text-[var(--color-overdue)]"
                        >
                          削除
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmingId('');
                          }}
                          className="rounded border border-[var(--color-border-subtle)] px-1.5 py-0.5 text-xs text-[var(--color-text-secondary)]"
                        >
                          戻る
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        aria-label={`${task.title}を削除`}
                        onClick={() => {
                          setConfirmingId(task.id);
                        }}
                        className="px-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-overdue)]"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      )}

      {creating ? (
        <MonthlyRunDialog
          month={month}
          items={tasks}
          periodSuffix={variant.periodSuffix}
          onCreate={(dateByRule) => {
            // 失敗したときの表示は画面上部の帯が担うので、ここでは待つだけにする
            void create(dateByRule);
          }}
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}

      {editing === undefined ? null : (
        <MonthlyFmtTaskEditor
          task={editing}
          defaults={newDefaults}
          suggestions={{
            businesses: listValues(items, (item) => item.business),
            categories: listValues(items, (item) => item.majorCategory),
            middleCategories: listValues(items, (item) => item.middleCategory),
            dueRules: listValues(items, (item) => item.dueRule),
            assignees: listValues(items, (item) => item.assignee),
          }}
          onSave={save}
          onDelete={editing === null ? undefined : removeItem}
          onClose={() => {
            setEditing(undefined);
          }}
        />
      )}

      <p className="mt-6 mb-0 text-xs leading-relaxed text-[var(--color-text-muted)]">
        この画面での追加・編集・削除は、そのまま保存されます（チーム共通）。営業日は土日だけを
        除いて数えており、祝日はまだ考慮していません。
      </p>
    </main>
  );
}
